import { Provider, Signal, Type, computed, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Shared ControlValueAccessor plumbing for every @oskey/ui form control
 * (<osk-input>, <osk-textarea>, <osk-checkbox>, <osk-switch>, <osk-time-input>, <osk-time-field>,
 * <osk-search-field>, <osk-select> / <osk-filter-trigger>, <osk-date-picker>).
 *
 * Subclasses declare their own `disabled` input and `writeValue`, call `onChangeFn(value)` when the
 * user changes the value and `onTouchedFn()` when they leave the control, and read `isDisabled()`,
 * which combines the `disabled` input with the form control's disabled state.
 * Register with `providers: [provideOSKValueAccessor(() => MyComponent)]`.
 */
export abstract class OSKValueAccessorBase<T> implements ControlValueAccessor {
  /** The control's own `disabled` input, declared by each subclass. */
  abstract readonly disabled: Signal<boolean>;

  private readonly formDisabled = signal<boolean>(false);

  /** True when either the `disabled` input or the bound form control disables the control. */
  protected readonly isDisabled = computed<boolean>(() => this.disabled() || this.formDisabled());

  protected onChangeFn: (value: T) => void = () => {};
  protected onTouchedFn: () => void = () => {};

  abstract writeValue(value: unknown): void;

  registerOnChange(fn: (value: T) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
}

/** `NG_VALUE_ACCESSOR` provider for a component extending OSKValueAccessorBase. */
export function provideOSKValueAccessor(component: () => Type<unknown>): Provider {
  return { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(component), multi: true };
}
